import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Box, Paper, Stack, useMediaQuery, useTheme } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useLocation, useNavigate } from "react-router-dom-v5-compat";
import { useCompany } from "../Shared/components/CompanyProvider/CompanyProvider";
import { convertToCartVisibilitySettings } from "../Shared/components/OrderSummary/OrderSummary.request";
import { OrderSummaryEditContext } from "../Shared/components/OrderSummary/OrderSummaryEditContext/OrderSummaryEditContext";
import Loading from "../Shared/components/loading";
import { LogLevel } from "../Shared/hooks/useLogging/Logging-types";
import { useLogging } from "../Shared/hooks/useLogging/useLogging";
import { useGetModel } from "./OrderFlowProvider/OrderFlow.requests";
import { ActionTypes } from "./OrderFlowProvider/OrderFlowActionTypes";
import { useOrderFlow } from "./OrderFlowProvider/OrderFlowContext";
import { OrderFlowText, PaperSettings } from "./OrderFlowProvider/OrderFlowTypes";
import { DEFAULT_CART } from "./OrderFlowProvider/defaultCart";
import { DEFAULT_MODEL } from "./OrderFlowProvider/defaultModel";
import { CartButton } from "./components/Cart/CartButton";
import { CartDrawer } from "./components/Cart/CartDrawer";
import { DraftOrderFlowText } from "./components/DraftOrderFlowText";
import { NavigationButtonContainer_V2 } from "./components/NavigationButtons/NavigationButtonContainer";
import { RecipeSelectionWarningModal } from "./components/RecipeSelectionWarningModal/RecipeSelectionWarningModal";
import { SubscriptionSummaryPage } from "./components/SubscriptionSummaryPage/SubscriptionSummaryPage";
import { OrderFlowErrorMessages, useSession } from "./hooks/Session/useSession";
import { useCalculationDebounce } from "./hooks/useCalculationDebounce";
import { useCheckRequirements } from "./hooks/useCheckRequirements";
import { useFlowModule } from "./hooks/useFlowModule";
import { useRecipeSelectionWarning } from "./hooks/useRecipeSelectionWarning";
import { getPortalRoot } from "./utils/orderFlowUtils";
const useStyles = makeStyles((theme) => ({
    mobileNavButtons: {
        alignItems: "stretch",
        height: "76px",
    },
    modulesContainer: {
        padding: 0,
        display: "flex",
        flexDirection: "column",
        flexGrow: 1,
        justifyContent: "flex-start",
        "&>.MuiBox-root": {
            width: "100%",
            maxWidth: 1280,
            margin: "0 auto",
        },
    },
}));
export const OrderFlow_V2 = ({ sku, editOrderId, dateChangesTakeEffect, subscriptionLinkId, startDate, subscriptionPeriod, closeEditModal, scrollToModalRef, }) => {
    var _a;
    // * STATE VALUES
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [showEditSubscriptionSummary, setShowEditSubscriptionSummary] = useState(false);
    const [isUpdatingPrice, setIsUpdatingPrice] = useState(false);
    const [isGettingCartSettings, setIsGettingCartSettings] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [requirementError, setRequirementError] = useState(undefined);
    const [showRecipeSelectionWarningModal, setShowRecipeSelectionWarningModal] = useState(false);
    // * HOOKS
    const pinnedToTopRefs = useRef([]);
    const theme = useTheme();
    const classes = useStyles();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"), { noSsr: true });
    // * CUSTOM HOOKS
    // TODO REPLACE AND REMOVE ALL INSTANCES OF AND REFERENCES TO pricingBreakdown WITH EQUIVALENT FROM BALANCE OBJECT
    const { store, dispatch, pricingBreakdown, setPricingBreakdown, balance, setBalance } = useOrderFlow();
    const { ContinueToCheckout, SaveOrderFlowSession, restartOrderFlow, restartEditFlow } = useSession();
    const { CheckRequirements } = useCheckRequirements();
    const { missingRecipeSelections, missingMealsMessages } = useRecipeSelectionWarning();
    const { loading } = useGetModel(sku, editOrderId, dateChangesTakeEffect, startDate, subscriptionPeriod);
    const { flowModulePaperMap, RenderModule, paperPadding, getRemovePaperPadding } = useFlowModule();
    const navigate = useNavigate();
    const location = useLocation();
    const debouncedStore = useCalculationDebounce(store, 1000);
    const { logReactPixelPageView, createKlaviyoEvent, gaPageView, datadogLog } = useLogging();
    const { isEditMode, resetContext: resetOrderSummaryEditContext, resetMealsAndTemplate, } = useContext(OrderSummaryEditContext);
    const { recipeSelectionWarningMessage, recipeSelectionWarningTitle } = store.model;
    // openedFromMobileApp is used to track whether or not the user is in an order
    // flow from the (SFF) mobile app. If true, the styles are adjusted for
    // a better mobile experience.
    const { openedFromMobileApp } = useCompany();
    const updatePinnedHeights = () => {
        var _a;
        let heightSum = 0;
        let nonPaperHeightSum = 0;
        let zIndex = 1100;
        (_a = pinnedToTopRefs.current) === null || _a === void 0 ? void 0 : _a.forEach((pinnedModule, index, arr) => {
            var _a, _b;
            const prevIsOnPaper = (_a = arr[index - 1]) === null || _a === void 0 ? void 0 : _a.closest(".MuiPaper-root:not(#item-card-accordion-root):not(#edit-flow-dialog)");
            if (prevIsOnPaper) {
                heightSum += nonPaperHeightSum;
            }
            pinnedModule.style.top = `${heightSum}px`;
            pinnedModule.style.zIndex = zIndex.toString();
            const height = Math.floor(pinnedModule.getBoundingClientRect().height);
            const nextPaperParentEl = (_b = arr[index + 1]) === null || _b === void 0 ? void 0 : _b.closest(".MuiPaper-root:not(#item-card-accordion-root):not(#edit-flow-dialog)");
            const nextPaperModuleIsOnSamePaper = nextPaperParentEl === null || nextPaperParentEl === void 0 ? void 0 : nextPaperParentEl.contains(pinnedModule);
            const isOnPaper = pinnedModule.closest(".MuiPaper-root:not(#item-card-accordion-root):not(#edit-flow-dialog)");
            if (!isOnPaper || nextPaperModuleIsOnSamePaper) {
                if (isOnPaper) {
                    heightSum = 0;
                }
                heightSum += height;
                zIndex--;
                if (!isOnPaper) {
                    nonPaperHeightSum += height;
                }
            }
            else {
                heightSum = 0;
                zIndex = 1100;
            }
        });
    };
    useEffect(() => {
        logReactPixelPageView();
        gaPageView("Order Flow", location.pathname);
        createKlaviyoEvent("PageView", {
            SelectedLocation: "OrderFlow",
        });
    }, []);
    useLayoutEffect(() => {
        updatePinnedHeights();
        window.addEventListener("resize", updatePinnedHeights);
        return () => {
            window.removeEventListener("resize", updatePinnedHeights);
        };
    });
    useEffect(() => {
        var _a, _b, _c;
        if (debouncedStore) {
            if (((_a = debouncedStore === null || debouncedStore === void 0 ? void 0 : debouncedStore.model) === null || _a === void 0 ? void 0 : _a.DeliveryDays) &&
                debouncedStore.cart.startDate !== "" &&
                (debouncedStore.cart.meals ||
                    ((_b = debouncedStore.cart.products) === null || _b === void 0 ? void 0 : _b.some((p) => p.quantity > 0)) ||
                    ((_c = debouncedStore.cart.template) === null || _c === void 0 ? void 0 : _c.length) > 0)) {
                setIsUpdatingPrice(true);
                SaveOrderFlowSession(debouncedStore.cart, debouncedStore.model, false)
                    .then((res) => {
                    var _a;
                    if (!((_a = res === null || res === void 0 ? void 0 : res.data) === null || _a === void 0 ? void 0 : _a.success)) {
                        dispatch({
                            type: ActionTypes.SET_CART,
                            payload: DEFAULT_CART,
                        });
                        dispatch({
                            type: ActionTypes.SET_MODEL,
                            payload: DEFAULT_MODEL,
                        });
                        store.model.IsEditFlow
                            ? restartEditFlow()
                            : restartOrderFlow(store.model.Sku, OrderFlowErrorMessages.REVIEWING_ORDER);
                    }
                    else {
                        if (res && res.data) {
                            // TODO REPLACE AND REMOVE ALL INSTANCES OF AND REFERENCES TO pricingBreakdown WITH EQUIVALENT FROM BALANCE OBJECT
                            if (res.data.pricingBreakdown) {
                                setPricingBreakdown(res.data.pricingBreakdown);
                            }
                            if (res.data.balance) {
                                setBalance(res.data.balance);
                            }
                        }
                    }
                    setIsUpdatingPrice(false);
                })
                    .catch((err) => {
                    dispatch({
                        type: ActionTypes.SET_CART,
                        payload: DEFAULT_CART,
                    });
                    dispatch({
                        type: ActionTypes.SET_MODEL,
                        payload: DEFAULT_MODEL,
                    });
                    setIsUpdatingPrice(false);
                    restartOrderFlow(store.model.Sku, OrderFlowErrorMessages.REVIEWING_ORDER);
                    datadogLog(`Caught exception in SaveOrderFlowSession. Message: ${err.message}`, LogLevel.ERROR, undefined, err);
                });
            }
            else {
                // TODO REPLACE AND REMOVE ALL INSTANCES OF AND REFERENCES TO pricingBreakdown WITH EQUIVALENT FROM BALANCE OBJECT
                setPricingBreakdown(undefined);
                setBalance(undefined);
                setIsUpdatingPrice(false);
            }
        }
    }, [
        debouncedStore.cart.startDate,
        debouncedStore.cart.meals,
        debouncedStore.cart.products,
        debouncedStore.cart.template,
        debouncedStore.cart.additionalItems,
        debouncedStore.model,
    ]);
    const toggleDrawer = (open) => (event) => {
        if (event.type === "keydown" &&
            (event.key === "Tab" || event.key === "Shift")) {
            return;
        }
        if (isEditMode) {
            resetMealsAndTemplate();
            resetOrderSummaryEditContext();
        }
        setIsDrawerOpen(open);
    };
    //Check requirements before going to next page
    const handleContinueClick = () => {
        const requirementsResponse = CheckRequirements(store.model.requirements, store.model.SkipPayment, store.cart.meals, store.cart.products, store.model.PostalCode, store.cart.currentPage, store.cart.daysOfWeek, store.model, store.cart);
        if (!(requirementsResponse === null || requirementsResponse === void 0 ? void 0 : requirementsResponse.meetsRequirements)) {
            setRequirementError(requirementsResponse === null || requirementsResponse === void 0 ? void 0 : requirementsResponse.errorMessage);
            return;
        }
        else {
            setRequirementError(undefined);
        }
        if (missingRecipeSelections) {
            setShowRecipeSelectionWarningModal(true);
        }
        else {
            setShowRecipeSelectionWarningModal(false);
            continueToNextPage();
        }
    };
    //If theres a next page go, otherwise save and get ready for checkout
    const continueToNextPage = () => {
        if (store.cart.currentPage === store.cart.totalPages) {
            setIsSaving(true);
            if (store.model.IsEditFlow) {
                setShowEditSubscriptionSummary(true);
            }
            else {
                ContinueToCheckout(store.cart, store.model)
                    .then((res) => {
                    if (res && res.success && res.continueToCheckout) {
                        const orderFlowState = {
                            // TODO REPLACE AND REMOVE ALL INSTANCES OF AND REFERENCES TO pricingBreakdown WITH EQUIVALENT FROM BALANCE OBJECT
                            pricingBreakdown: res.pricingBreakdown,
                            balance: res.balance,
                            orderInfo: res.orderInfo,
                            sku: store.model.Sku,
                            location: store.model.Location,
                            postalCode: store.model.PostalCode,
                        };
                        setIsSaving(false);
                        datadogLog(`User went from page ${store.cart.currentPage} of Order Flow to Checkout. SKU: ${store.model.Sku}`, LogLevel.INFO);
                        navigate({
                            pathname: `/${store.model.Sku}/Checkout`,
                        }, { state: Object.assign(Object.assign({}, location.state), { orderFlowState }) });
                    }
                    else {
                        restartOrderFlow(store.model.Sku, OrderFlowErrorMessages.REVIEWING_ORDER);
                        datadogLog("ContinueToCheckout failed.", LogLevel.ERROR);
                    }
                })
                    .catch((err) => {
                    datadogLog(`Caught exception in ContinueToCheckout. Message: ${err.message}`, LogLevel.ERROR, undefined, err);
                    restartOrderFlow(store.model.Sku, OrderFlowErrorMessages.REVIEWING_ORDER);
                });
            }
        }
        else {
            setIsDrawerOpen(false);
            (scrollToModalRef === null || scrollToModalRef === void 0 ? void 0 : scrollToModalRef.current) ? scrollToModalRef.current.scrollTo(0, 0) : window.scrollTo(0, 0);
            pinnedToTopRefs.current = [];
            dispatch({ type: ActionTypes.SET_RECIPE_FETCH_STATUS, payload: { error: false, completed: false } });
            dispatch({ type: ActionTypes.SET_CURRENT_PAGE, payload: store.cart.currentPage + 1 });
            datadogLog(`User went from page ${store.cart.currentPage} to page ${store.cart.currentPage + 1} of Order Flow. SKU: ${store.model.Sku}`, LogLevel.INFO);
            updatePinnedHeights();
        }
    };
    const handlePreviousClick = () => {
        if (store.cart.currentPage === 1) {
            datadogLog(`User went back to the Location page. SKU: ${store.model.Sku}`, LogLevel.INFO);
            navigate(`/${store.model.Sku}`, {
                state: { location: store.model.Location, postalCode: store.model.PostalCode },
            });
        }
        else {
            setIsSaving(false);
            setRequirementError(undefined);
            (scrollToModalRef === null || scrollToModalRef === void 0 ? void 0 : scrollToModalRef.current) ? scrollToModalRef.current.scrollTo(0, 0) : window.scrollTo(0, 0);
            pinnedToTopRefs.current = [];
            dispatch({
                type: ActionTypes.SET_RECIPE_FETCH_STATUS,
                payload: { error: false, completed: false },
            });
            dispatch({ type: ActionTypes.SET_CURRENT_PAGE, payload: store.cart.currentPage - 1 });
            datadogLog(`User went from page ${store.cart.currentPage} to page ${store.cart.currentPage - 1} of Order Flow. SKU: ${store.model.Sku}`, LogLevel.INFO);
            updatePinnedHeights();
        }
    };
    const portalRoot = getPortalRoot(store.model.IsEditFlow, (_a = store.model.OrderFlowSettings) === null || _a === void 0 ? void 0 : _a.PinNavButtons);
    if (Boolean(loading)) {
        return _jsx(Loading, { text: loading, viewportHeight: "75vh" });
    }
    if (showEditSubscriptionSummary) {
        return (_jsx(Stack, { children: _jsx(SubscriptionSummaryPage, { setIsUpdatingPrice: setIsUpdatingPrice, onBackButtonClick: () => {
                    setIsSaving(false);
                    setShowEditSubscriptionSummary(false);
                }, mobileNavButtonsClass: classes.mobileNavButtons, subscriptionLinkId: subscriptionLinkId, closeEditModal: closeEditModal, isBalanceLoading: isUpdatingPrice }) }));
    }
    return (_jsxs(_Fragment, { children: [_jsx(Box, Object.assign({ flexGrow: 1 }, { children: flowModulePaperMap.map((parentModule, index) => {
                    var _a, _b;
                    //Visible is undefined when a module doesnt have that config so it is always visible
                    const isParentModuleVisible = parentModule.ModuleConfig.Visible || parentModule.ModuleConfig.Visible === undefined;
                    const parentModulePinToTopId = `${parentModule.Module.ModuleType}-PinnedToTop-P${index}`;
                    const parentModuleIsBanner = parentModule.ModuleName == "Banner";
                    const bannerIsBottomOfPaper = ((_a = parentModule.ModulesOnSamePaper.slice(-1)[0]) === null || _a === void 0 ? void 0 : _a.ModuleName) == "Banner" ||
                        (parentModuleIsBanner && parentModule.ModulesOnSamePaper.length == 0);
                    return parentModule.ModuleConfig.PaperSetting === PaperSettings.NoPaper ||
                        parentModule.ModuleConfig.PaperSetting === undefined ? (
                    //Not on paper so render module with configured spacing above and below
                    //Apply left and right padding to match margins of modules on paper
                    isParentModuleVisible && (_jsx(Box, Object.assign({ id: parentModule.ModuleConfig.PinToTop ? parentModulePinToTopId : undefined, position: parentModule.ModuleConfig.PinToTop && "sticky", zIndex: 100, width: "100%", ref: (el) => {
                            var _a, _b;
                            if (el && !((_a = pinnedToTopRefs.current) === null || _a === void 0 ? void 0 : _a.find((thisEl) => el.id === thisEl.id))) {
                                parentModule.ModuleConfig.PinToTop && ((_b = pinnedToTopRefs.current) === null || _b === void 0 ? void 0 : _b.push(el));
                            }
                        } }, { children: _jsx(Box, Object.assign({ px: parentModuleIsBanner ? 0 : theme.spacing(2), py: (_b = parentModule.ModuleConfig.SpacingAbove) !== null && _b !== void 0 ? _b : theme.spacing(2) }, { children: RenderModule(parentModule) })) }), parentModule.Sequence))) : (
                    // ! On paper, render paper with constant spacing AND sub modules with configured spacing
                    _jsxs(Paper, Object.assign({ sx: {
                            padding: paperPadding,
                            paddingTop: parentModuleIsBanner ? "0px" : paperPadding,
                            paddingBottom: bannerIsBottomOfPaper ? "0px" : paperPadding,
                            marginTop: theme.spacing(3),
                            marginBottom: theme.spacing(3),
                        }, elevation: openedFromMobileApp ? 0 : 2 }, { children: [isParentModuleVisible && (_jsx(Box, Object.assign({ id: parentModule.ModuleConfig.PinToTop ? parentModulePinToTopId : undefined, position: parentModule.ModuleConfig.PinToTop && "sticky", zIndex: 100, bgcolor: parentModule.ModuleConfig.PinToTop && "white", ref: (el) => {
                                    var _a, _b;
                                    if (el && !((_a = pinnedToTopRefs.current) === null || _a === void 0 ? void 0 : _a.find((thisEl) => el.id === thisEl.id))) {
                                        parentModule.ModuleConfig.PinToTop && ((_b = pinnedToTopRefs.current) === null || _b === void 0 ? void 0 : _b.push(el));
                                    }
                                } }, { children: _jsx(Box, Object.assign({ pb: parentModule.ModuleConfig.SpacingBelow, pt: parentModule.ModuleConfig.SpacingAbove, sx: {
                                        overflowY: "clip",
                                    } }, { children: RenderModule(parentModule, getRemovePaperPadding(parentModule)) }), parentModule.Sequence) }), parentModule.Sequence)), parentModule.ModulesOnSamePaper.map((subModule, index) => {
                                const subModulePinToTopId = `${subModule.Module.ModuleType}-PinnedToTop-S${index}`;
                                const isSubModuleVisible = subModule.ModuleConfig.Visible || subModule.ModuleConfig.Visible === undefined;
                                return (isSubModuleVisible && (_jsx(Box, Object.assign({ id: subModule.ModuleConfig.PinToTop ? subModulePinToTopId : "", position: subModule.ModuleConfig.PinToTop && "sticky", zIndex: 100, bgcolor: subModule.ModuleConfig.PinToTop && "white", ref: (el) => {
                                        var _a, _b;
                                        if (el && !((_a = pinnedToTopRefs.current) === null || _a === void 0 ? void 0 : _a.find((thisEl) => el.id === thisEl.id))) {
                                            subModule.ModuleConfig.PinToTop && ((_b = pinnedToTopRefs.current) === null || _b === void 0 ? void 0 : _b.push(el));
                                        }
                                    } }, { children: _jsx(Box, Object.assign({ pb: subModule.ModuleConfig.SpacingBelow, pt: subModule.ModuleConfig.SpacingAbove }, { children: RenderModule(subModule, getRemovePaperPadding(subModule)) })) }), subModule.ModuleConfig.PinToTop ? subModulePinToTopId : subModule.Sequence)));
                            })] }), parentModule.Sequence));
                }) })), store.model.Flow.isDraft && _jsx(DraftOrderFlowText, { text: OrderFlowText.DraftOrder }), showRecipeSelectionWarningModal && (_jsx(RecipeSelectionWarningModal, Object.assign({}, {
                continueToNextPage,
                missingMealsMessages,
                recipeSelectionWarningMessage,
                recipeSelectionWarningTitle,
                setShowRecipeSelectionWarningModal,
                showRecipeSelectionWarningModal,
            }))), 
            // We move the navigation bar outside the order flow and into the root layout.
            // This moves the navigation bar outside of the app container, allowing it to
            // be full screen and sticky.
            // We are leaving this here because it relies heavily on coupled logic.
            portalRoot &&
                ReactDOM.createPortal(_jsx(NavigationButtonContainer_V2, { onContinueClick: handleContinueClick, onPreviousClick: handlePreviousClick, loading: isSaving, requirementError: requirementError, isUpdatingPrice: isUpdatingPrice || isGettingCartSettings, pricingBreakdown: pricingBreakdown, balance: balance, onClick: toggleDrawer(true), isGettingCartSettings: isGettingCartSettings }), portalRoot), !store.model.IsEditFlow && !isMobile && (_jsx(CartButton, { isUpdatingPrice: isUpdatingPrice || isGettingCartSettings, 
                // TODO REPLACE AND REMOVE ALL INSTANCES OF AND REFERENCES TO pricingBreakdown WITH EQUIVALENT FROM BALANCE OBJECT
                pricingBreakdown: pricingBreakdown, balance: balance, onClick: toggleDrawer(true) })), _jsx(CartDrawer, { isUpdatingPrice: isUpdatingPrice, isSaving: isSaving, isDrawerOpen: isDrawerOpen, toggleDrawer: toggleDrawer, onContinueClick: handleContinueClick, balance: balance, cartSettings: convertToCartVisibilitySettings(store.model.CartSettings, store.model.SkipPayment) })] }));
};
